import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, tap, map } from 'rxjs/operators';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { IGetUsers, IGetRolesOrFeature, IGetUserGroups, getAssetsNAclsByGroupId, IGetAssetsNAcls } from 'app/services/graphqlApi.service';
import { getAssetsNAclsByUserGroupId, getFeatureByName, getFeatures, getFeaturesTreeView, getRoleByName, getRoles, getUserById, getUserGroupById, getUserGroups, getUserGroupsWithMembers, getUsers } from 'app/shared/constants/gql';
import { RestAPIService } from '../services/rest-api.service';
import { RouteNavLockService } from 'app/services/route-nav-lock.service';
import { IBasic } from 'app/shared/services/common.service';
import { IAssetsNAcls, IFeaturesTree, IMembers, IRoleOrFeature, IUser, IUserGroup } from './user-management.model';
import { IDropdown } from 'app/shared/interfaces/model';
import { RestRequestType } from '../shared/enums/rest-request-type.enum';
import { EUserManagmentTabs } from 'app/shared/enums';
// import { Apollo } from 'apollo-angular';

@Injectable({ providedIn: 'root' })

export class UserManagementService {
  constructor(private readonly apiService: RestAPIService, readonly toastr: ToastrService,
    // private readonly apollo: Apollo,
    private readonly router: Router,
    private readonly restApiService: RestAPIService,
    private readonly routeNavLockService: RouteNavLockService) { }

  // for user-group's assets permissions tab
  readonly _assetsAcls = new BehaviorSubject<IAssetsNAcls>(null);
  readonly assetsAcls$ = this._assetsAcls.asObservable();

  // checked Acls for usergroups's permission page
  readonly _checkedAcls = new BehaviorSubject<IDropdown[]>([]);
  readonly checkedAcls$ = this._checkedAcls.asObservable();

    // features view data
    readonly _featuresViewData = new BehaviorSubject<IFeaturesTree[]>([]);

  //#region CRUD Users

  /**
   * fetch all users
   */
  getUsers() {
    return this.apiService.getGQLData({ query: getUsers.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetUsers[] = response.data?.intellija_users;
      return (queryResponse || []).map(record => this.setUserData(record) || [])
    }));
  }

  /**
   * fetch user by id
   * @param userId user id
   */
  getUser(userId: string) {
    return this.apiService.getGQLData({ query: getUserById.query, params: { userId: userId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetUsers[] = response.data?.intellija_users;
      return (queryResponse || []).map(record => this.setUserData(record));
    }));
  }

  /**
   * add new user
   * @param user user
   */
  createUser(user: IUser) {
    return this.apiService.post<IUser>('users', RestRequestType.CREATE_USER, user)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('User created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
 * update user record
 * @param user user
 */
  updateUser(user: IUser) {
    return this.apiService.put<IUser>(`users/${user.id}`, RestRequestType.UPDATE_USER, user)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('User updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  deleteUserById(userId: string) {
    return this.apiService.delete(`users/${userId}`)
      .toPromise().then(res => {
        this.toastr.success('User deleted successfully!');
        // this.loadAllModels();
        return Promise.resolve();
      }, err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return Promise.reject(err.error);
      });
  }

  //#endregion

  //#region CRUD Roles

  /**
    * fetch roles
    */
  getRoles() {
    return this.apiService.getGQLData({ query: getRoles.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetRolesOrFeature[] = response.data?.intellija_roles;
      return (queryResponse || []).map(record => this.setRolesOrFeaturesData(record));
    }));
  }

  /**
  * fetch role by name
  * @param roleName role name
  */
  getRole(roleName: string) {
    return this.apiService.getGQLData({ query: getRoleByName.query, params: { roleName: roleName } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetRolesOrFeature[] = response.data?.intellija_roles;
      return (queryResponse || []).map(record => this.setRolesOrFeaturesData(record));
    }));
  }

  /**
 * add new role
 * @param role role
 */
  createRole(role: IRoleOrFeature) {
    return this.apiService.post<IRoleOrFeature>('roles', RestRequestType.CREATE_ROLE, role)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
        if (res.result.status === 'ok') {
          this.toastr.success('Role created successfully!');
        } else {
          this.toastr.error(res.error.message);
        }
      }), map(res => res.data));
  }

  /**
  * update role record
  * @param role role
  */
  updateRole(role: IRoleOrFeature) {
    return this.apiService.put<IRoleOrFeature>(`roles`, RestRequestType.UPDATE_ROLE, role)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
        if (res.result.status === 'ok') {
          this.toastr.success('Role updated successfully!');
        } else {
          this.toastr.error(res.error.message);
        }
      }), map(res => res.data));
  }

  /**
 * delete role
 * @param rolePayload role row payload
 */
  deleteRole(rolePayload: any) {
    return this.apiService.delete(`roles`, { body: rolePayload }).toPromise().then(res => {
      this.toastr.success('Role deleted successfully!');
      return Promise.resolve();
    }, err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return Promise.reject(err.error);
    });
  }

  //#endregion

  //#region CRUD Features

  /**
  * fetch features
  */
  getFeatures() {
    return this.apiService.getGQLData({ query: getFeatures.query })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response) => {
        const queryResponse: IGetRolesOrFeature[] = response.data?.intellija_ref_features;
        return (queryResponse || []).map(record => this.setFeatureData(record) || []);
      }));
  }

  getFeaturesTreeData() {
    return this.apiService.getGQLData({ query: getFeaturesTreeView.query })
    .pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      return (response.data?.intellija_features_tree_view[0]?.view || [])as IFeaturesTree[];
    }));
  }

  /**
  * fetch feature by name
  * @param featureName feature name
  */
  getFeature(featureName: string) {
    return this.apiService.getGQLData({ query: getFeatureByName.query, params: { featureName: featureName } })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response) => {
        const queryResponse: IGetRolesOrFeature[] = response.data?.intellija_ref_features;
        return (response.data?.intellija_ref_features || []).map(record => this.setFeatureData(record) || []);
      }));
  }

  /**
 * add new feature
 * @param feature feature
 */
  createFeature(feature: IRoleOrFeature) {
    return this.apiService.post<IGetRolesOrFeature>('features', RestRequestType.CREATE_FEATURE, feature)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Feature created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
  * update feature record
  * @param feature feature
  */
  updateFeature(feature: IRoleOrFeature) {
    return this.apiService.put<IGetRolesOrFeature>(`features`, RestRequestType.UPDATE_FEATURE, feature)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
        if (res.result.status === 'ok') {
          this.toastr.success('Feature updated successfully!');
        } else {
          this.toastr.error(res.error.message);
        }
      }), map(res => res.data));
  }

  /**
 * delete feature
 * @param featurePayload feature row payload
 */
  deleteFeature(featurePayload: any) {
    return this.apiService.delete(`features`, { body: featurePayload }).toPromise().then(res => {
      this.toastr.success('Feature deleted successfully!');
      return Promise.resolve();
    }, err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return Promise.reject(err.error);
    });
  }

  //#endregion

  //#region CRUD User Groups

  /**
  * fetch all User Groups
  */
  getUserGroups() {
    return this.apiService.getGQLData({ query: getUserGroups.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetUserGroups[] = response.data?.intellija_user_groups;
      return (queryResponse || []).map(groupData => this.setUserGroupData(groupData));
    }));
  }

  /**
   * fetch user group by id
   * @param groupId group id
   */
  getUserGroupById(groupId: string) {
    return this.apiService.getGQLData({
      query: getUserGroupById.query, params: { groupId: groupId }
    }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetUserGroups[] = response.data?.intellija_user_groups;
      return (queryResponse || []).map(groupData => this.setUserGroupData(groupData));
    }));
  }

  /**
  * add new user Group
  * @param userGroup user_group
  */
  createUserGroup(userGroup: IUserGroup) {
    console.log('userGroup: ', userGroup);
    return this.apiService.post<IGetUserGroups>('user-groups', RestRequestType.CREATE_USER_GROUP, userGroup)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('User group created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
  * update user Group
  * @param userGroup user_group
  */
  updateUserGroup(userGroup: IUserGroup) {
    return this.apiService.put<IGetUserGroups>(`user-groups/${userGroup.id}`, RestRequestType.UPDATE_USER_GROUP, userGroup)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('User group updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
* delete user group
* @param groupId group id
*/
  deleteUserGroupById(groupId: string) {
    return this.apiService.delete(`user-groups/${groupId}`).toPromise().then(res => {
      this.toastr.success('User group deleted successfully!');
      return Promise.resolve();
    }, err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return Promise.reject(err.error);
    });
  }

  //#endregion

  //#region View ORM

  /**
   * set roles data
   * @param record record
   * @param isFeature (true/false)
   */
  setRolesOrFeaturesData(record: IGetRolesOrFeature, isFeature: boolean = false): IRoleOrFeature {
    const objFeature: IRoleOrFeature = {
      name: record.name,
      displayName: record.display_name,
      features: record.features,
      createdBy: record.created_by,
      createdOn: record.created_date,
      updatedBy: record.updated_by,
      updatedOn: record.updated_date
    };

    if (isFeature) {
      delete objFeature.features;
    }

    return objFeature;
  }

  /**
   * set user group data
   * @param groupData group data
   */
  setUserGroupData(groupData: IGetUserGroups) {
    return {
      id: groupData.id,
      name: groupData.name,
      path: groupData.path,
      createdBy: groupData.created_by,
      createdOn: groupData.created_date,
      updatedBy: groupData.updated_by,
      updatedOn: groupData.updated_date,
      realm: groupData.realm,
      roles: groupData.roles,
      members: this.getMembers(groupData.user_group_members),
    } as IUserGroup;
  }

  getMembers(members): IMembers[] {
    return (members || []).map(m => ({
      userId: m.user.id,
      groupId: m.user_group_id,
      username: m.user.username,
    }));
  }

  /**
   * set features data
   * @param record user record
   */
  setFeatureData(record: IGetRolesOrFeature) {
    return {
      name: record.name,
      displayName: record.display_name,
      createdBy: record.created_by,
      createdOn: record.created_date,
      updatedBy: record.updated_by,
      updatedOn: record.updated_date
    } as IRoleOrFeature;
  }

  /**
   * set user data
   * @param record user record
   */
  setUserData(record: IGetUsers) {
    return {
      id: record.id,
      userName: record.username,
      firstName: record.first_name,
      middleName: record.middle_name,
      lastName: record.last_name,
      mobileNo: record.mobile_no,
      email: record.email,
      isActive: record.is_active,
      customerId: record.customer_id,
      createdBy: record.created_by,
      createdOn: record.created_date,
      updatedBy: record.updated_by,
      updatedOn: record.updated_date
    } as IUser;
  }

  //#endregion


  /**
* add new Members to  Group
* @param userGroup user_group
*/
  addMembers(members: IMembers) {
    console.log('members: ', members);
    return this.apiService.post<IMembers>('user-group-members/add', RestRequestType.ADD_GROUP_MEMBER, members)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Group members added successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
* Remove Members from Group
* @param userGroup user_group
*/
  removeMembers(members: IMembers) {
    console.log('removeMembers: ', members);
    return this.apiService.post<IMembers>('user-group-members/remove', RestRequestType.REMOVE_GROUP_MEMBER, members)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Group members removed successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
  * fetch all User Groups
  */
  getAllUserGroups() {
    return this.apiService.getGQLData({ query: getUserGroupsWithMembers.query }).pipe(map(response => {
      const queryResponse: IGetUserGroups[] = response.data?.intellija_user_groups;
      return (queryResponse || []).map(groupData => this.setUserGroupData(groupData));
    }));
  }

  /**
   * get assets list and acls by userGroupId (two queries in one call)
   * @param userGroupId user group id
   */
  getAssetsNAclsByGroupId(userGroupId: string) {
      return this.apiService.getGQLData({
        query: getAssetsNAclsByUserGroupId.query, params: { userGroupId: userGroupId } }).pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response) => { return response?.data as IGetAssetsNAcls }));
  }

  addCheckedAcl(assetId: string, aclType: string) {
    const checkedAcls: IDropdown[] = this._checkedAcls.getValue();
    if(checkedAcls.findIndex(acl => acl.value === assetId && aclType === acl.name) === -1){
      checkedAcls.push({ name: aclType, value: assetId });
    }

    // console.log('addCheckedAcl: ', checkedAcls);
    this._checkedAcls.next(checkedAcls);
  }

  removeUncheckedAcl(assetId: string, aclType: string) {
    const checkedAcls: IDropdown[] = this._checkedAcls.getValue();
    const index: number = checkedAcls.findIndex(acl => acl.value === assetId && aclType === acl.name);
    if(index > -1){
      checkedAcls.splice(index, 1);
      this._checkedAcls.next(checkedAcls);
    }
  }

  /**
   * delete deleteUserManagementItem user/user-group or role
   * @param infoPayload info payload
   * @param navigateToPage navigate to page
   */
  deleteUserManagementItem(infoPayload: IBasic, navigateToPage: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const subUser = this.routeNavLockService.showRouteNavPrompt('This action is irreversible!')
        .subscribe(res => {
          subUser.unsubscribe();
          if (res) {
            if (navigateToPage === EUserManagmentTabs.Users) {
              this.deleteUserById(infoPayload.id)
                .then(result => {
                  resolve(true); // Resolve the promise with true
                }).catch(error => reject(error)); // Reject the promise if there's an error
            } else if (navigateToPage === EUserManagmentTabs.Roles) {
              const body = { name: infoPayload.name };
              const payload = this.restApiService.buildPayload(body, RestRequestType.DELETE_ROLE);
              this.deleteRole(payload).then(result => {
                  resolve(true); // Resolve the promise with true
                })
                .catch(error => reject(error)); // Reject the promise if there's an error
            } else {
              this.deleteUserGroupById(infoPayload.id)
                .then(result => {
                  resolve(true); // Resolve the promise with true
                }).catch(error => reject(error)); // Reject the promise if there's an error
            }
          } else {
            resolve(false); // Resolve the promise with false if the user cancels the action
          }
        });
    });
  }

}